import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { TabNav } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { useSession } from '@/features/auth/api/useSession'
import { useCases } from '@/features/cases/api/useCase'
import { newClientKey } from '@/features/cases/api/useCaseChat'
import { useQuotes } from '@/features/quotes/api/useQuote'
import { ChatPanel } from '@/features/cases/components/ChatPanel'
import { ConversationList } from '@/features/chat/components/ConversationList'
import { ChatThreadHeader } from '@/features/chat/components/ChatThreadHeader'
import {
  labelKey,
  type RecordLabel,
  type RecordLabels,
} from '@/features/chat/lib/recordLabel'
import { useChatSocketContext } from '@/features/chat/api/chatSocketContext'
import {
  useConversation,
  useConversations,
  useMarkRead,
  useSendMessage,
} from '@/features/chat/api/useConversation'
import { cn } from '@/lib/cn'
import type { ChatSubjectKind, Conversation } from '@/types/domain'

/* The chat page (0029, 0052): every conversation the reader can reach, in two
 * tabs.
 *
 * The tab is a SEARCH PARAM rather than a nested route, following the house
 * convention: the surrounding chrome stays mounted across a switch, so the
 * thread beside the list does not unmount and remount when somebody moves
 * between quotes and cases.
 *
 * The panel is `ChatPanel` unchanged — it was already caller-agnostic, taking
 * messages and an onSend and knowing nothing about where they came from. What
 * differs per tab is `allowAttachments`, which is false on quotes: there is no
 * quote file store, and a paperclip that uploaded bytes no message could point
 * at would be a photo that appears to send and is then unreachable forever.
 *
 * Two panes, two CARDS (0052). Both are bounded boxes that scroll their own
 * contents rather than growing the page: a fifty-message thread beside a
 * twenty-row list would otherwise put one scrollbar around both, and reading
 * the conversation would scroll the list away from the row naming it.
 *
 * `?subject=` is the second half of the URL contract, and it exists for the
 * record dock: its expand control navigates to `/chat?tab=…&subject=<id>` so
 * that "open this in full" lands on the conversation somebody was already
 * reading rather than on whatever is newest. The two per-tab state variables
 * below remain the source of truth; the URL is a mirror of them and an entry
 * point into them.
 */

const TABS = [
  { key: 'cases', labelKey: 'chat.tabs.cases' },
  { key: 'quotes', labelKey: 'chat.tabs.quotes' },
] as const

type TabKey = (typeof TABS)[number]['key']

/** The tab a subject kind belongs to. The wire spells them singular; the tabs
 *  are plural, and mapping in one place keeps the two from drifting. */
const KIND_FOR_TAB: Record<TabKey, ChatSubjectKind> = {
  cases: 'case',
  quotes: 'quote',
}

/* How tall the two cards stand at lg and up.
 *
 * The shell is `h-dvh overflow-hidden` with a scrolling <main>, so the figure
 * subtracted here is that main's own chrome above and below this grid: its
 * top padding, the page header, the tab bar, the two 20px column gaps and the
 * bottom padding. It is a constant rather than a measurement because a
 * ResizeObserver driving a height is a layout that reflows on every keystroke
 * in the composer.
 *
 * The floor matters as much as the height: on a short laptop the calc would
 * otherwise leave a thread shorter than its own composer. */
const PANE_HEIGHT = 'lg:h-[calc(100dvh-var(--spacing-topbar)-13rem)] lg:min-h-[26rem]'

export function ChatPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = searchParams.get('tab')
  const tab: TabKey = raw === 'quotes' ? 'quotes' : 'cases'

  const conversations = useConversations()

  /* The record behind each conversation, for its label.
   *
   * The list endpoint sends the SUBJECT pair — a kind and an id — and nothing
   * human. Resolving "AB-27, Nurana Trading" needs the record, and these are
   * the two lists the reader can already see: both are cached by the pages
   * they belong to, so on a warm client this costs nothing, and on a cold one
   * it is two requests the reader was one click from making anyway.
   *
   * Deliberately NOT a per-row fetch. Twenty conversations would be twenty
   * requests to render a list, which is the shape NFR-2 rules out — and the
   * server cannot fold the labels in without collab reading two neighbouring
   * modules' tables, which is the boundary the ports exist to hold.
   *
   * The STATUS rides along for the same reason and at the same price: the two
   * summaries already carry it, so a chip on the row costs nothing beyond the
   * two reads that were happening regardless. */
  const cases = useCases()
  const quotes = useQuotes()

  const labels: RecordLabels = useMemo(() => {
    const byId = new Map<string, RecordLabel>()
    for (const c of cases.data ?? []) {
      byId.set(labelKey('case', c.id), {
        number: c.number,
        clientName: c.clientName,
        status: { kind: 'case', value: c.status },
      })
    }
    for (const q of quotes.data ?? []) {
      byId.set(labelKey('quote', q.id), {
        number: q.number,
        clientName: q.clientName,
        status: { kind: 'quote', value: q.status },
      })
    }
    return byId
  }, [cases.data, quotes.data])

  /* Which thread is open, per tab.
   *
   * Two pieces of state rather than one, because switching tabs must not carry
   * a case's selection onto the quote list — where it would name a
   * conversation that tab cannot show, and the panel would ask the wrong
   * endpoint for it.
   *
   * They are the tab's MEMORY rather than its live selection. What is live is
   * `?subject=`, which is what makes the URL an entry point rather than a copy
   * kept in step by an effect: a page that synchronised the two would be
   * calling setState from an effect on every navigation, which is a cascading
   * render for something that can simply be derived. */
  const [openCase, setOpenCase] = useState<string | null>(null)
  const [openQuote, setOpenQuote] = useState<string | null>(null)

  /* The active tab's selection: what the URL names, or where this tab was last
     left. Derived on every render from those two, never stored a third time. */
  const subjectParam = searchParams.get('subject')
  const remembered = tab === 'cases' ? openCase : openQuote
  const selectedId = subjectParam ?? remembered

  const kind = KIND_FOR_TAB[tab]
  const rows = useMemo(
    () => (conversations.data ?? []).filter((c) => c.subjectKind === kind),
    [conversations.data, kind],
  )

  const selected =
    rows.find((c) => c.subjectId === selectedId) ??
    /* Nothing chosen yet: open the most recently active, which the server
       already sorted to the front. A page that opened blank would make the
       common case — "what happened while I was out" — cost an extra click.

       Computed over the UNFILTERED rows on purpose. The list's search box is a
       view of the list and nothing more: narrowing it must not close the
       conversation somebody is reading, which is what would happen if the
       fallback picked the first row still matching what they were typing. */
    rows[0]

  const select = (subjectId: string) => {
    if (tab === 'cases') setOpenCase(subjectId)
    else setOpenQuote(subjectId)

    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    next.set('subject', subjectId)
    setSearchParams(next, { replace: true })
  }

  const label = selected
    ? labels.get(labelKey(selected.subjectKind, selected.subjectId))
    : undefined

  return (
    <>
      <PageHeader title={t('chat.title')} subtitle={t('chat.subtitle')} />

      <TabNav
        label={t('chat.title')}
        items={TABS}
        value={tab}
        onChange={(key) => {
          if (key === tab) return

          /* Leave a bookmark in the tab being left, so coming back reopens the
             conversation somebody was reading rather than resetting to the
             newest one. This is why the two state variables exist at all. */
          if (tab === 'cases') setOpenCase(subjectParam)
          else setOpenQuote(subjectParam)

          const next = new URLSearchParams(searchParams)
          next.set('tab', key)
          /* The incoming tab's OWN bookmark, never the outgoing one's: a case
             id left in the param would point the quote tab at a record it
             cannot show, and the panel beside it would ask the case endpoint
             for a quote. */
          const bookmark = key === 'cases' ? openCase : openQuote
          if (bookmark) next.set('subject', bookmark)
          else next.delete('subject')
          setSearchParams(next, { replace: true })
        }}
      />

      <TabPanel tabKey={tab}>
        {conversations.isLoading ? (
          <div className="grid gap-2" aria-label={t('chat.loading')}>
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : conversations.isError ? (
          <ErrorState error={conversations.error} />
        ) : (
          <div
            className={cn(
              'grid min-h-0 gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]',
              PANE_HEIGHT,
            )}
          >
            <ConversationList
              rows={rows}
              labels={labels}
              kind={kind}
              selectedId={selected?.subjectId ?? null}
              emptyLabel={
                tab === 'cases' ? t('chat.emptyCases') : t('chat.emptyQuotes')
              }
              onSelect={select}
            />

            {selected ? (
              <Thread
                key={selected.id}
                conversation={selected}
                title={label?.number ?? selected.subjectId.slice(0, 8)}
                clientName={label?.clientName}
              />
            ) : (
              <div
                className={cn(
                  'bg-surface border-line flex items-center justify-center',
                  'rounded-[var(--radius-lg)] border p-8',
                  /* Below lg the pair is stacked, and an empty box as tall as
                     the viewport would push the list off a phone screen. */
                  'max-lg:py-10',
                )}
              >
                <p className="text-text-muted text-[13px] font-light">
                  {t('chat.pickOne')}
                </p>
              </div>
            )}
          </div>
        )}
      </TabPanel>
    </>
  )
}

function Thread({
  conversation,
  title,
  clientName,
}: {
  conversation: Conversation
  title: string
  clientName?: string
}) {
  const { t } = useTranslation()
  const { data: session } = useSession()

  const kind = conversation.subjectKind
  const thread = useConversation(kind, conversation.subjectId)
  const socket = useChatSocketContext()

  /* Watch this thread while it is open (0029).
   *
   * Subscribing per OPEN THREAD rather than to everything: the socket is
   * multiplexed, and a client that joined every room would be told about
   * conversations nobody is looking at — which costs the server a fan-out per
   * message per idle tab.
   *
   * The unsubscribe on cleanup is what makes switching threads cheap. It is
   * refcounted inside the socket, so a second panel on the same conversation
   * does not cut this one off when it closes. */
  useEffect(() => {
    socket.subscribe(conversation.id)
    return () => socket.unsubscribe(conversation.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id])
  const send = useSendMessage(kind, conversation.subjectId)
  const markRead = useMarkRead()

  /* Move the watermark to whatever is on screen.
   *
   * Fired on the last message's id rather than on a scroll position: the
   * server refuses to move a watermark backwards, so reporting the newest
   * held message is both correct and idempotent.
   *
   * A PENDING bubble is skipped, as in `ChatDock`. Its id is the client key,
   * which names no row the server has, so reporting it spends a request to
   * earn a refusal on every message this reader sends. The effect re-fires
   * with the real id the moment the send resolves. */
  const newest = thread.messages[thread.messages.length - 1]
  const lastSeen = useRef<string | null>(null)
  useEffect(() => {
    if (!newest || newest.pending || lastSeen.current === newest.id) return
    lastSeen.current = newest.id
    markRead.mutate({ conversationId: conversation.id, messageId: newest.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest?.id, newest?.pending, conversation.id])

  return (
    <div
      className={cn(
        'bg-surface border-line flex min-h-0 flex-col overflow-hidden',
        'rounded-[var(--radius-lg)] border',
        /* Below lg the card is not inside a viewport-height grid, so it needs
           a height of its own — ChatPanel is `h-full` and would otherwise
           collapse onto its composer. */
        'max-lg:h-[32rem]',
      )}
    >
      <ChatThreadHeader
        kind={kind}
        subjectId={conversation.subjectId}
        title={title}
        clientName={clientName}
      />

      {conversation.archived ? (
        <p className="border-line text-text-muted m-3 mb-0 shrink-0 rounded-[var(--radius-md)] border px-3 py-2 text-[12.5px]">
          <strong className="text-text-heading font-medium">
            {t('chat.archived')}
          </strong>{' '}
          {t('chat.archivedWhy')}
        </p>
      ) : null}

      {/* No global mutation error handler exists, so an unread `.error` is a
          silent refusal — every mutation on this page renders its own. */}
      {send.error ? (
        <div className="shrink-0 px-3 pt-3">
          <ErrorState error={send.error} />
        </div>
      ) : null}

      <ChatPanel
        messages={thread.messages}
        currentUserId={session?.id ?? ''}
        currentUserAvatarUrl={session?.avatarUrl}
        isPending={thread.isLoading}
        isError={thread.isError}
        hasMore={thread.hasMore}
        isLoadingMore={thread.isLoadingMore}
        onLoadMore={thread.loadEarlier}
        /* False on quotes: no quote file store exists, so a paperclip here
           would upload bytes no message could ever point at. */
        allowAttachments={kind === 'case' && !conversation.archived}
        /* The client key is minted here, once per message, exactly as
           `ChatDock` does: it is both the request's idempotency key and the
           optimistic bubble's id, so the send hook requires it rather than
           defaulting it. */
        onSend={({ body }) => send.mutate({ body, clientKey: newClientKey() })}
        className="min-h-0 flex-1"
      />
    </div>
  )
}
